///
/// EarthBrowser
///
/// Created by Matt Giger on 10/16/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Night-lights raster tiles (NASA Black Marble) served from the tile-server
/// nightlights.pmtiles archive, built by tools/night_lights/build.ts. The
/// archive is cut on the earth tiles' own equirectangular grid so each
/// EarthTile samples exactly one night tile, but PMTiles ids assume a square
/// grid: equirect level z lives at archive zoom z + 1, so the archive's
/// maxzoom is one more than the finest equirect level available.
///

import { type TileId } from "@/earthbrowser/activities/EarthActivity/tilecache/TileId"
import { logger } from "@/utils/LogDispatcher"

// Set VITE_TILE_SERVER_URL (e.g. https://localhost:5174) to use a local tile-server
const cTileServerUrl = import.meta.env.VITE_TILE_SERVER_URL || "https://tiles.earthbrowser.com"
const cSource = "nightlights"
// Used when the archive's TileJSON can't be read (matches a ~13.5k px source)
const cFallbackMaxLevel = 5

let maxLevelPromise: Promise<number> | undefined

/**
 * Finest equirect level the archive holds tiles for, from the archive's
 * TileJSON. Fetched once; failures fall back to a constant so the lights
 * still load.
 */
export function nightLightsMaxLevel(): Promise<number> {
	maxLevelPromise ??= fetch(`${cTileServerUrl}/${cSource}/metadata`)
		.then(async response => {
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`)
			}
			const tilejson = await response.json() as { maxzoom?: number }
			return typeof tilejson.maxzoom === "number" ? tilejson.maxzoom - 1 : cFallbackMaxLevel
		})
		.catch(error => {
			logger.warn(`Night lights metadata unavailable, assuming level ${cFallbackMaxLevel}: ${error}`)
			return cFallbackMaxLevel
		})
	return maxLevelPromise
}

/** Night tile covering an earth tile (archive zoom = equirect level + 1) */
export function nightLightsTileUrl(tileId: TileId): string {
	return `${cTileServerUrl}/${cSource}/${tileId.z + 1}/${tileId.x}/${tileId.y}.webp`
}
